import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { HomepageDesignSystemSettings } from '../components/sections/sectionRegistry';
import {
  DEFAULT_COLLECTION_CARD_TEMPLATES,
  normalizeCollectionCardTemplates,
} from '../lib/collectionCardTemplates';
import {
  DEFAULT_DESIGN_SYSTEM_TYPOGRAPHY,
  DEFAULT_FONT_LIBRARY,
  DEFAULT_HERO_READABILITY_DEFAULTS,
  DEFAULT_TYPOGRAPHY_PRESETS,
  normalizeTypographyPreset,
} from '../lib/typography';

type Mode = 'published' | 'draft';

interface SiteSettingsRow<T = any> {
  key: string;
  content?: T | null;
  draft_content?: T | null;
  published_content?: T | null;
}

const DESIGN_SYSTEM_KEY = 'design_system';

export const DEFAULT_DESIGN_SYSTEM_SETTINGS: HomepageDesignSystemSettings = {
  sectionSpacing: 'md',
  containerMaxWidth: 'xl',
  sectionBackground: 'default',
  mutedBackgroundColor: '#f5f5f4',
  accentBackgroundColor: '#eff6ff',
  fontLibrary: DEFAULT_DESIGN_SYSTEM_TYPOGRAPHY.fontLibrary,
  presets: DEFAULT_DESIGN_SYSTEM_TYPOGRAPHY.presets,
  heroReadabilityDefaults: DEFAULT_DESIGN_SYSTEM_TYPOGRAPHY.heroReadabilityDefaults,
  collectionCardTemplates: DEFAULT_COLLECTION_CARD_TEMPLATES,
};

export function normalizeDesignSystemSettings(value: unknown): HomepageDesignSystemSettings {
  const raw = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const normalized: HomepageDesignSystemSettings = { ...DEFAULT_DESIGN_SYSTEM_SETTINGS };

  if (raw.sectionSpacing === 'none' || raw.sectionSpacing === 'sm' || raw.sectionSpacing === 'md' || raw.sectionSpacing === 'lg' || raw.sectionSpacing === 'xl') {
    normalized.sectionSpacing = raw.sectionSpacing;
  }

  if (raw.containerMaxWidth === 'full' || raw.containerMaxWidth === 'xl' || raw.containerMaxWidth === 'lg' || raw.containerMaxWidth === 'md') {
    normalized.containerMaxWidth = raw.containerMaxWidth;
  }

  if (raw.sectionBackground === 'default' || raw.sectionBackground === 'muted' || raw.sectionBackground === 'accent') {
    normalized.sectionBackground = raw.sectionBackground;
  }

  if (typeof raw.mutedBackgroundColor === 'string' && raw.mutedBackgroundColor.trim()) {
    normalized.mutedBackgroundColor = raw.mutedBackgroundColor.trim();
  }

  if (typeof raw.accentBackgroundColor === 'string' && raw.accentBackgroundColor.trim()) {
    normalized.accentBackgroundColor = raw.accentBackgroundColor.trim();
  }

  const fontLibrary =
    raw.fontLibrary && typeof raw.fontLibrary === 'object' && !Array.isArray(raw.fontLibrary)
      ? Object.fromEntries(
          Object.entries(raw.fontLibrary as Record<string, unknown>).filter(
            ([key, value]) => typeof key === 'string' && !!key.trim() && typeof value === 'string' && !!value.trim()
          )
        )
      : null;

  normalized.fontLibrary = {
    ...DEFAULT_FONT_LIBRARY,
    ...(fontLibrary ?? {}),
  };

  const rawPresets =
    raw.presets && typeof raw.presets === 'object' && !Array.isArray(raw.presets)
      ? (raw.presets as Record<string, unknown>)
      : {};

  normalized.presets = {
    heroTitle: normalizeTypographyPreset(rawPresets.heroTitle, DEFAULT_TYPOGRAPHY_PRESETS.heroTitle, normalized.fontLibrary),
    heroSubtitle: normalizeTypographyPreset(rawPresets.heroSubtitle, DEFAULT_TYPOGRAPHY_PRESETS.heroSubtitle, normalized.fontLibrary),
    heroMicroLine: normalizeTypographyPreset(rawPresets.heroMicroLine, DEFAULT_TYPOGRAPHY_PRESETS.heroMicroLine, normalized.fontLibrary),
    sectionHeading: normalizeTypographyPreset(rawPresets.sectionHeading, DEFAULT_TYPOGRAPHY_PRESETS.sectionHeading, normalized.fontLibrary),
    body: normalizeTypographyPreset(rawPresets.body, DEFAULT_TYPOGRAPHY_PRESETS.body, normalized.fontLibrary),
    button: normalizeTypographyPreset(rawPresets.button, DEFAULT_TYPOGRAPHY_PRESETS.button, normalized.fontLibrary),
    caption: normalizeTypographyPreset(rawPresets.caption, DEFAULT_TYPOGRAPHY_PRESETS.caption, normalized.fontLibrary),
  };

  const rawReadability =
    raw.heroReadabilityDefaults &&
    typeof raw.heroReadabilityDefaults === 'object' &&
    !Array.isArray(raw.heroReadabilityDefaults)
      ? (raw.heroReadabilityDefaults as Record<string, unknown>)
      : {};
  normalized.heroReadabilityDefaults = {
    backdrop:
      rawReadability.backdrop === 'none' || rawReadability.backdrop === 'subtle' || rawReadability.backdrop === 'strong'
        ? rawReadability.backdrop
        : DEFAULT_HERO_READABILITY_DEFAULTS.backdrop,
    textShadow:
      rawReadability.textShadow === 'none' || rawReadability.textShadow === 'soft' || rawReadability.textShadow === 'strong'
        ? rawReadability.textShadow
        : DEFAULT_HERO_READABILITY_DEFAULTS.textShadow,
    autoContrast:
      typeof rawReadability.autoContrast === 'boolean'
        ? rawReadability.autoContrast
        : DEFAULT_HERO_READABILITY_DEFAULTS.autoContrast,
  };

  normalized.collectionCardTemplates = normalizeCollectionCardTemplates(
    raw.collectionCardTemplates ?? DEFAULT_COLLECTION_CARD_TEMPLATES
  );

  return normalized;
}

function resolvePublished(
  row: SiteSettingsRow<HomepageDesignSystemSettings> | null | undefined
): HomepageDesignSystemSettings {
  const published = row?.published_content;
  if (published != null) return normalizeDesignSystemSettings(published);
  if (import.meta.env.DEV) {
    return normalizeDesignSystemSettings(row?.content ?? DEFAULT_DESIGN_SYSTEM_SETTINGS);
  }
  return DEFAULT_DESIGN_SYSTEM_SETTINGS;
}

function resolveDraft(
  row: SiteSettingsRow<HomepageDesignSystemSettings> | null | undefined
): HomepageDesignSystemSettings {
  return normalizeDesignSystemSettings(
    row?.draft_content ?? row?.published_content ?? row?.content ?? DEFAULT_DESIGN_SYSTEM_SETTINGS
  );
}

export function useDesignSystemSettings(mode: Mode = 'published') {
  const [settings, setSettings] = useState<HomepageDesignSystemSettings>(DEFAULT_DESIGN_SYSTEM_SETTINGS);
  const [loading, setLoading] = useState(true);

  const selectColumns = useMemo(
    () =>
      mode === 'draft'
        ? 'key, content, draft_content, published_content'
        : import.meta.env.DEV
          ? 'key, content, published_content'
          : 'key, published_content',
    [mode]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('site_settings')
          .select(selectColumns)
          .eq('key', DESIGN_SYSTEM_KEY)
          .maybeSingle<SiteSettingsRow<HomepageDesignSystemSettings>>();

        if (cancelled) return;
        if (error) {
          if (import.meta.env.DEV) console.warn('[useDesignSystemSettings] Falling back to defaults', error);
          setSettings(DEFAULT_DESIGN_SYSTEM_SETTINGS);
          return;
        }

        setSettings(mode === 'draft' ? resolveDraft(data) : resolvePublished(data));
      } catch (error) {
        if (!cancelled && import.meta.env.DEV) {
          console.warn('[useDesignSystemSettings] Failed to load design system settings', error);
        }
        if (!cancelled) setSettings(DEFAULT_DESIGN_SYSTEM_SETTINGS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [mode, selectColumns]);

  return { settings, loading };
}

export type { HomepageDesignSystemSettings };
